import type { WeaponSystem, WeaponUnit, Arsenal } from '../types/military';
import type { Side } from '../types/game-state';

// US Weapon Systems
const MINUTEMAN_III: WeaponSystem = {
  type: 'icbm',
  name: 'MINUTEMAN III',
  yieldKt: 300,
  cepMeters: 120,
  rangeKm: 13000,
  flightTimeMinutes: 30,
  survivalRate: 0.0,  // vulnerable in silos during counterattack
  isRecallable: false,
  mirvCount: 3,
};

const TRIDENT_II: WeaponSystem = {
  type: 'slbm',
  name: 'TRIDENT II D5',
  yieldKt: 475,
  cepMeters: 90,
  rangeKm: 12000,
  flightTimeMinutes: 15,
  survivalRate: 0.9,  // submarines are highly survivable
  isRecallable: false,
  mirvCount: 8,
};

const B52_ALCM: WeaponSystem = {
  type: 'bomber',
  name: 'B-52H (ALCM)',
  yieldKt: 150,
  cepMeters: 30,
  rangeKm: 16000,
  flightTimeMinutes: 360,
  survivalRate: 0.5,  // can be intercepted
  isRecallable: true,
  mirvCount: 20,
};

const B2_SPIRIT: WeaponSystem = {
  type: 'bomber',
  name: 'B-2 SPIRIT',
  yieldKt: 1200,
  cepMeters: 20,
  rangeKm: 11000,
  flightTimeMinutes: 300,
  survivalRate: 0.8,  // stealth advantage
  isRecallable: true,
  mirvCount: 16,
};

// USSR Weapon Systems
const SS18_SATAN: WeaponSystem = {
  type: 'icbm',
  name: 'SS-18 SATAN',
  yieldKt: 750,
  cepMeters: 220,
  rangeKm: 16000,
  flightTimeMinutes: 28,
  survivalRate: 0.0,
  isRecallable: false,
  mirvCount: 10,
};

const SS_N_23: WeaponSystem = {
  type: 'slbm',
  name: 'SS-N-23 SKIFF',
  yieldKt: 100,
  cepMeters: 500,
  rangeKm: 8300,
  flightTimeMinutes: 18,
  survivalRate: 0.85,
  isRecallable: false,
  mirvCount: 4,
};

const TU95_BEAR: WeaponSystem = {
  type: 'bomber',
  name: 'TU-95 BEAR',
  yieldKt: 200,
  cepMeters: 50,
  rangeKm: 15000,
  flightTimeMinutes: 420,
  survivalRate: 0.4,
  isRecallable: true,
  mirvCount: 6,
};

function createWeaponUnits(
  system: WeaponSystem,
  count: number,
  side: Side,
  baseId: string,
): WeaponUnit[] {
  return Array.from({ length: count }, (_, i) => ({
    id: `${side.toLowerCase()}-${system.name.toLowerCase().replace(/[\s()/-]/g, '')}-${i}`,
    system,
    baseId,
    status: 'ready' as const,
    assignedTargetId: null,
  }));
}

function createUSArsenal(): Arsenal {
  const weapons: WeaponUnit[] = [
    // 150 Minuteman III ICBMs across 3 bases
    ...createWeaponUnits(MINUTEMAN_III, 50, 'US', 'us-malmstrom'),
    ...createWeaponUnits(MINUTEMAN_III, 50, 'US', 'us-minot'),
    ...createWeaponUnits(MINUTEMAN_III, 50, 'US', 'us-warren'),
    // 48 Trident II SLBMs across 2 submarine bases
    ...createWeaponUnits(TRIDENT_II, 24, 'US', 'us-kingsbay'),
    ...createWeaponUnits(TRIDENT_II, 24, 'US', 'us-bangor'),
    // 30 B-52H bombers
    ...createWeaponUnits(B52_ALCM, 15, 'US', 'us-barksdale'),
    ...createWeaponUnits(B52_ALCM, 15, 'US', 'us-dyess'),
    // 10 B-2 Spirit bombers
    ...createWeaponUnits(B2_SPIRIT, 10, 'US', 'us-whiteman'),
  ];

  return { side: 'US', weapons };
}

function createUSSRArsenal(): Arsenal {
  const weapons: WeaponUnit[] = [
    // 120 SS-18 Satan ICBMs across 5 bases
    ...createWeaponUnits(SS18_SATAN, 24, 'USSR', 'ussr-kozelsk'),
    ...createWeaponUnits(SS18_SATAN, 24, 'USSR', 'ussr-dombarovsky'),
    ...createWeaponUnits(SS18_SATAN, 24, 'USSR', 'ussr-uzhur'),
    ...createWeaponUnits(SS18_SATAN, 24, 'USSR', 'ussr-tatishchevo'),
    ...createWeaponUnits(SS18_SATAN, 24, 'USSR', 'ussr-aleysk'),
    // 40 SS-N-23 SLBMs across 3 bases
    ...createWeaponUnits(SS_N_23, 14, 'USSR', 'ussr-gadzhiyevo'),
    ...createWeaponUnits(SS_N_23, 14, 'USSR', 'ussr-vilyuchinsk'),
    ...createWeaponUnits(SS_N_23, 12, 'USSR', 'ussr-severodvinsk'),
    // 30 Tu-95 Bear bombers
    ...createWeaponUnits(TU95_BEAR, 15, 'USSR', 'ussr-engels'),
    ...createWeaponUnits(TU95_BEAR, 15, 'USSR', 'ussr-ukrainka'),
  ];

  return { side: 'USSR', weapons };
}

function getReadyWeapons(arsenal: Arsenal): WeaponUnit[] {
  return arsenal.weapons.filter(w => w.status === 'ready');
}

function getWeaponCount(arsenal: Arsenal): {
  total: number;
  ready: number;
  icbm: number;
  slbm: number;
  bomber: number;
  warheads: number;
} {
  const ready = arsenal.weapons.filter(w => w.status === 'ready');
  return {
    total: arsenal.weapons.length,
    ready: ready.length,
    icbm: ready.filter(w => w.system.type === 'icbm').length,
    slbm: ready.filter(w => w.system.type === 'slbm').length,
    bomber: ready.filter(w => w.system.type === 'bomber').length,
    warheads: ready.reduce((sum, w) => sum + w.system.mirvCount, 0),
  };
}

export {
  createUSArsenal,
  createUSSRArsenal,
  getReadyWeapons,
  getWeaponCount,
  MINUTEMAN_III,
  TRIDENT_II,
  B52_ALCM,
  B2_SPIRIT,
  SS18_SATAN,
  SS_N_23,
  TU95_BEAR,
};
